import { useState } from 'react';
import { Alert, FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import { ProjectSearchAutocomplete } from './ProjectSearchAutocomplete';
import { projectClassEmployeeNote, type SimpleRecord } from './receiptHelpers';
import type { ReceiptAllocationChanges, ReceiptCategory } from './receiptApi';

export type Allocation = {
    allocationType: 'project_cost' | 'company_expense';
    projectId: string;
    qboClassId: string;
    category: string;
    subcategory: string;
};

export const allocationFrom = (source: {
    allocation_type?: Allocation['allocationType'] | null;
    project_id?: number | null;
    qbo_class_id?: string | null;
    category?: string | null;
    subcategory?: string | null;
}): Allocation => ({
    allocationType: source.allocation_type || 'company_expense',
    projectId: source.project_id ? String(source.project_id) : '',
    qboClassId: source.qbo_class_id || '',
    category: source.category || '',
    subcategory: source.subcategory || '',
});

export const allocationChanges = (value: Allocation): ReceiptAllocationChanges => ({
    allocation_type: value.allocationType,
    project_id: value.allocationType === 'project_cost' ? Number(value.projectId) : null,
    qbo_class_id:
        value.allocationType === 'project_cost' && value.qboClassId
            ? value.qboClassId
            : null,
    category: value.category || null,
    subcategory: value.subcategory || null,
});

const buildingClasses = (project: SimpleRecord | null) => {
    if (project?.qbo_classes && project.qbo_classes.length > 0) {
        return project.qbo_classes;
    }
    const ids = project?.qbo_class_ids?.length
        ? project.qbo_class_ids
        : project?.qbo_class_id
          ? [project.qbo_class_id]
          : [];
    return ids.map(qbo_id => ({ qbo_id, name: qbo_id }));
};

// QuickBooks needs an expense account on every line, project costs included.
export const allocationReady = (value: Allocation, categories: ReceiptCategory[], project?: SimpleRecord | null) => {
    const selected = categories.find(item => item.name === value.category);
    const classes = buildingClasses(project ?? null);
    const needsBuilding = value.allocationType === 'project_cost' && classes.length > 1;
    return (
        !!value.category &&
        (!selected?.subcategories.length || !!value.subcategory) &&
        (value.allocationType !== 'project_cost' || !!value.projectId) &&
        (!needsBuilding || !!value.qboClassId)
    );
};

export const AllocationFields = ({
    value,
    onChange,
    categories,
    /** When true, show the employee note if the job has no Roofing Class yet. */
    showClassNote = false,
    onProjectSelect,
}: {
    value: Allocation;
    onChange: (value: Allocation) => void;
    categories: ReceiptCategory[];
    showClassNote?: boolean;
    onProjectSelect?: (project: SimpleRecord | null) => void;
}) => {
    const selectedCategory = categories.find(item => item.name === value.category);
    const [selectedProject, setSelectedProject] = useState<SimpleRecord | null>(null);
    const classes = buildingClasses(selectedProject);
    const classNote =
        showClassNote && value.allocationType === 'project_cost'
            ? projectClassEmployeeNote(selectedProject)
            : null;
    return (
        <>
            <FormControl required>
                <InputLabel>Allocation type</InputLabel>
                <Select
                    value={value.allocationType}
                    label="Allocation type"
                    onChange={event =>
                        onChange({
                            ...value,
                            allocationType: event.target.value as Allocation['allocationType'],
                            projectId: '',
                            qboClassId: '',
                        })
                    }
                >
                    <MenuItem value="project_cost">Project cost (COGS)</MenuItem>
                    <MenuItem value="company_expense">Company expense</MenuItem>
                </Select>
            </FormControl>
            {value.allocationType === 'project_cost' && (
                <ProjectSearchAutocomplete
                    value={value.projectId}
                    onChange={projectId => onChange({ ...value, projectId, qboClassId: '' })}
                    onProjectSelect={project => {
                        setSelectedProject(project);
                        onProjectSelect?.(project);
                        const options = buildingClasses(project);
                        if (options.length === 1 && !value.qboClassId) {
                            onChange({
                                ...value,
                                projectId: project ? String(project.id) : value.projectId,
                                qboClassId: options[0].qbo_id,
                            });
                        }
                    }}
                    required
                />
            )}
            {value.allocationType === 'project_cost' && classes.length > 1 && (
                <FormControl required>
                    <InputLabel>Building (QuickBooks Class)</InputLabel>
                    <Select
                        value={value.qboClassId}
                        label="Building (QuickBooks Class)"
                        onChange={event =>
                            onChange({ ...value, qboClassId: event.target.value })
                        }
                    >
                        {classes.map(item => (
                            <MenuItem key={item.qbo_id} value={item.qbo_id}>
                                {item.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
            {classNote && (
                <Alert severity="info">{classNote}</Alert>
            )}
            <FormControl required>
                <InputLabel>Expense category</InputLabel>
                <Select
                    value={value.category}
                    label="Expense category"
                    onChange={event =>
                        onChange({ ...value, category: event.target.value, subcategory: '' })
                    }
                >
                    {categories.map(item => (
                        <MenuItem key={item.name} value={item.name}>
                            {item.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {!!selectedCategory?.subcategories.length && (
                <FormControl required>
                    <InputLabel>Expense subcategory</InputLabel>
                    <Select
                        value={value.subcategory}
                        label="Expense subcategory"
                        onChange={event =>
                            onChange({ ...value, subcategory: event.target.value })
                        }
                    >
                        {selectedCategory.subcategories.map(item => (
                            <MenuItem key={item} value={item}>
                                {item}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </>
    );
};
